// /apps/provider/src/pages/Login.jsx

import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getEnvironmentConfig } from '@/config/platforms';

export function LoginPage() {
  const {
    loginWithRedirect,
    isAuthenticated,
    isLoading,
    error
  } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  // Get platform configuration
  const config = getEnvironmentConfig();

  // Optional ?connection=<auth0 connection name> forces a specific Auth0
  // connection (e.g. an enterprise SSO connection) for piloting before
  // domain-based routing is enabled on the tenant
  const ssoConnection = new URLSearchParams(location.search).get('connection');
  
  // Check if signup should be shown
  const showSignup = config.platform.id === 'rehab' || config.platform.id === 'exercise';

  // Auth0Provider handles the redirect callback automatically — this page
  // only needs to route authenticated users onward
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const returnTo = location.state?.returnTo || '/';
      navigate(returnTo, { replace: true });
    }
  }, [isLoading, isAuthenticated]);

  const handleLogin = () => {
    // Return path restored by PostLoginRedirect after the Auth0 round-trip
    sessionStorage.setItem('post_login_return_to', location.state?.returnTo || '/');
    loginWithRedirect({
      authorizationParams: {
        scope: 'openid profile email offline_access',
        ...(ssoConnection ? { connection: ssoConnection } : {}),
      }
    });
  };

  // Show loading state while processing authentication or redirecting onward
  if (isLoading || isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center">
      <div className="max-w-md w-full mx-auto px-4">
        <div className="bg-white py-8 px-6 shadow-md rounded-lg">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">{config.appName}</h2>
            <p className="mt-2 text-gray-600">
              {showSignup ? 'Sign in or create an account' : 'Please sign in to your account'}
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-700">{error.message || 'Sign-in could not be completed.'}</p>
            </div>
          )}

          <button
            onClick={handleLogin}
            className="w-full py-3 px-4 bg-primary text-white rounded-lg font-medium hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            style={{ backgroundColor: config.themeColor }}
          >
            Sign In
          </button>
          
          {showSignup && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>
              
              <button
                onClick={() => navigate(`/onboarding/${config.platform.id}`)}
                className="w-full py-3 px-4 border border-primary text-primary rounded-lg font-medium hover:bg-primary/5 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                style={{ borderColor: config.themeColor, color: config.themeColor }}
              >
                Create Account
              </button>
              
              <p className="mt-4 text-center text-xs text-gray-500">
                By creating an account, you agree to our{' '}
                <a href="https://rehabilitationhealth.com/terms-of-use" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="text-primary hover:underline">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="https://rehabilitationhealth.com/privacy-policy" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="text-primary hover:underline">
                  Privacy Policy
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}